import { CareTeamSlot } from '../../../../episodes/data/episodesApi'
import { ADDITIONAL_SUPPORT_SLOT_KEY, getAdditionalSupportFormSlotId } from '../getManageCareTeamFormSlots'

export enum CareTeamChangeType {
  END = 'end',
  REASSIGN = 'reassign',
  ASSIGN = 'assign',
}

export type CareTeamSelections = Record<string, string[]>

export type CareTeamChange =
  | { type: CareTeamChangeType.END; careTeamSlotAssignmentId: string }
  | { type: CareTeamChangeType.REASSIGN; careTeamSlotAssignmentId: string; newProviderLyraId: string }
  | { type: CareTeamChangeType.ASSIGN; providerLyraId: string; careTeamSlots: string[] }

export const getCareTeamChanges = (slots: CareTeamSlot[], selections: CareTeamSelections): CareTeamChange[] => {
  const ends: CareTeamChange[] = []
  const reassigns: CareTeamChange[] = []
  const newSlotsByProvider = new Map<string, string[]>()

  slots.forEach((slot) => {
    const selectedProviders = selections[slot.care_team_slot]
    if (!selectedProviders) {
      return
    }

    const desired = new Set(selectedProviders)
    const assignedProviders = new Set(slot.assignments.map(({ provider_lyra_id }) => provider_lyra_id))
    const removed = slot.assignments.filter(({ provider_lyra_id }) => !desired.has(provider_lyra_id))
    const added = [...desired].filter((providerLyraId) => !assignedProviders.has(providerLyraId))

    removed.forEach(({ care_team_slot_assignment_id }, index) => {
      const replacement = added[index]
      if (replacement) {
        reassigns.push({
          type: CareTeamChangeType.REASSIGN,
          careTeamSlotAssignmentId: care_team_slot_assignment_id,
          newProviderLyraId: replacement,
        })
      } else {
        ends.push({ type: CareTeamChangeType.END, careTeamSlotAssignmentId: care_team_slot_assignment_id })
      }
    })

    added.slice(removed.length).forEach((providerLyraId) => {
      newSlotsByProvider.set(providerLyraId, [...(newSlotsByProvider.get(providerLyraId) ?? []), slot.care_team_slot])
    })
  })

  const assigns: CareTeamChange[] = [...newSlotsByProvider].map(([providerLyraId, careTeamSlots]) => ({
    type: CareTeamChangeType.ASSIGN,
    providerLyraId,
    careTeamSlots,
  }))

  return [...reassigns, ...assigns, ...ends]
}

export const getCareTeamSelections = (
  slots: CareTeamSlot[],
  formSelections: Record<string, string>,
): CareTeamSelections => {
  const selections: CareTeamSelections = {}

  slots.forEach((slot) => {
    if (slot.care_team_slot === ADDITIONAL_SUPPORT_SLOT_KEY) {
      const isEdited = slot.assignments.some(
        (assignment) => getAdditionalSupportFormSlotId(assignment) in formSelections,
      )
      if (isEdited) {
        selections[slot.care_team_slot] = slot.assignments.map(
          (assignment) => formSelections[getAdditionalSupportFormSlotId(assignment)] ?? assignment.provider_lyra_id,
        )
      }
      return
    }

    const selected = formSelections[slot.care_team_slot]
    if (selected) {
      const untouchedProviders = slot.assignments.slice(1).map(({ provider_lyra_id }) => provider_lyra_id)
      selections[slot.care_team_slot] = [...new Set([selected, ...untouchedProviders])]
    }
  })

  return selections
}
